/**
 * Retry strategy hook for exponential backoff
 * Follows ADR-003: High Cohesion - Single responsibility: manage retry timing
 */

import { useRef, useCallback, useEffect } from 'react';
import { log } from '../utils/logger';
import { ErrorCategory } from './useErrorCategorization';

/** Default maximum retry duration: 30 seconds */
export const DEFAULT_MAX_RETRY_DURATION = 30000;

/**
 * Options for useRetryStrategy hook
 */
export interface UseRetryStrategyOptions {
  /** Maximum time to retry before giving up (default: 30000ms) */
  maxRetryDuration?: number;
  /** Callback when retry timeout is reached */
  onTimeout?: () => void;
}

/**
 * Return value for useRetryStrategy hook
 */
export interface UseRetryStrategyReturn {
  /** Whether connection has timed out */
  hasTimedOut: boolean;
  /** Get elapsed time since first retry */
  getElapsedTime: () => number;
  /** Calculate next retry delay based on error category */
  calculateRetryDelay: (category: ErrorCategory) => number;
  /** Schedule a retry callback */
  scheduleRetry: (callback: () => void, delay: number) => void;
  /** Reset all retry state */
  reset: () => void;
  /** Check if we should stop retrying */
  shouldStopRetrying: () => boolean;
  /** Mark retry start time */
  markRetryStart: () => void;
}

/**
 * Custom hook for managing retry timing with exponential backoff
 * 
 * @param options - Retry configuration options
 * @returns Retry management functions
 */
export function useRetryStrategy({
  maxRetryDuration = DEFAULT_MAX_RETRY_DURATION,
  onTimeout
}: UseRetryStrategyOptions = {}): UseRetryStrategyReturn {
  const retryStartTimeRef = useRef<number | null>(null);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasTimedOutRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(false);
  const retryCountRef = useRef<number>(0); // Track actual retry attempts

  // Track mount state
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Marks the start of retry attempts
   */
  const markRetryStart = useCallback(() => {
    if (retryStartTimeRef.current === null) {
      retryStartTimeRef.current = Date.now();
    }
  }, []);

  /**
   * Gets elapsed time since first retry attempt
   */
  const getElapsedTime = useCallback((): number => {
    if (retryStartTimeRef.current === null) {
      return 0;
    }
    return Date.now() - retryStartTimeRef.current;
  }, []);

  /**
   * Checks if we should stop retrying
   */
  const shouldStopRetrying = useCallback((): boolean => {
    if (hasTimedOutRef.current) {
      return true;
    }
    
    const elapsed = getElapsedTime();
    if (elapsed >= maxRetryDuration) {
      log(`Retry timeout after ${maxRetryDuration}ms`);
      hasTimedOutRef.current = true;
      onTimeout?.();
      return true;
    }
    
    return false;
  }, [getElapsedTime, maxRetryDuration, onTimeout]);

  /**
   * Calculates retry delay based on error category and retry count
   * Uses exponential backoff with jitter for better distributed retries
   */
  const calculateRetryDelay = useCallback((category: ErrorCategory): number => {
    // Increment retry count
    retryCountRef.current += 1;
    const retryCount = retryCountRef.current;
    
    // Add small random jitter (0-500ms) to prevent thundering herd
    const jitter = Math.random() * 500;
    
    if (category === ErrorCategory.RATE_LIMITED) {
      // Rate limiting - longer delays (5s to 60s)
      const baseDelay = Math.min(5000 * Math.pow(2, retryCount - 1), 60000);
      const delay = baseDelay + jitter;
      log(`Rate limited - retry #${retryCount}, delay: ${Math.round(delay)}ms`);
      return delay;
    }
    
    // Transient errors - standard backoff (3s to 30s)
    // Start at 3s minimum to avoid hammering the server
    const baseDelay = Math.min(3000 * Math.pow(1.5, retryCount - 1), 30000);
    const delay = baseDelay + jitter;
    log(`Transient error - retry #${retryCount}, delay: ${Math.round(delay)}ms`);
    return delay;
  }, []);

  /**
   * Schedules a retry callback
   */
  const scheduleRetry = useCallback((callback: () => void, delay: number): void => {
    // Clear any existing timeout
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    
    retryTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current) {
        callback();
      }
    }, delay);
  }, []);

  /**
   * Resets all retry state
   */
  const reset = useCallback((): void => {
    log('Resetting retry state');
    retryStartTimeRef.current = null;
    hasTimedOutRef.current = false;
    retryCountRef.current = 0;
    
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  return {
    hasTimedOut: hasTimedOutRef.current,
    getElapsedTime,
    calculateRetryDelay,
    scheduleRetry,
    reset,
    shouldStopRetrying,
    markRetryStart
  };
}

